"use client";

/**
 * SoftwarePartRepoEditor — where a SoftwarePart's code lives (coding harness
 * stage 3): its repository and the paths in it.
 *
 * Registered as a field override on `repo_paths` (overrides attach to
 * fields, and `realized_by_repo` is a relationship), so "which repo" and
 * "where in it" are edited together:
 *
 *   - a picker of the organisation's CONNECTED repos (generated client:
 *     `rflp-organisation-github-repos`, judged by the normal predicate) that
 *     sets `realized_by_repo` through the editable relationship manager —
 *     `{set: [{id}]}`, what the SoftwarePart modify construction spec lists;
 *     the server refuses any repo not connected to the project's own
 *     organisation;
 *   - the `repo_paths` list, one directory or file per line (`pinned_ref`
 *     keeps its generic field).
 *
 * Everything saves through the registry (`editable.save()` → /api/modify).
 * Empty: the part inherits its parent's repository (`effective_repo`).
 * Connecting new repos is for owners and admins, on /connect-repo.
 */

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { graphStore } from "@sourcepilot/framework/graph-store";
import { GitHubRepo } from "@sourcepilot/generated/rflp/api/githubrepo";
import { SoftwarePart } from "@sourcepilot/generated/rflp/api/softwarepart";
import { QueryNames } from "@sourcepilot/generated/query-names";
import { readActiveOrganisationId } from "@sourcepilot/ui/framework/data/github-connection";
import { toRefusal, type Refusal } from "@sourcepilot/ui/framework/data/refusal";
import { RefusalNotice } from "@sourcepilot/ui/framework/ui/molecules/RefusalNotice";

type RepoOption = { id: string; name: string; private?: boolean; connected?: boolean };

interface Props {
  editable: any;
  node: any;
}

/** One path per line; blank lines and surrounding spaces dropped. */
function parsePaths(text: string): string[] {
  return text
    .split("\n")
    .map((p) => p.trim())
    .filter(Boolean);
}

/** The repo the part links itself (`realized_by_repo`), as the graph store
 *  holds it; null when it holds none. */
export function currentRepoId(node: any): string | null {
  const id = node?.id;
  if (!id) return null;
  try {
    return (graphStore.getTargetNodes<any>(id, "realized_by_repo")[0] as any)?.id ?? null;
  } catch {
    return null;
  }
}

export function SoftwarePartRepoEditor({ editable, node }: Props) {
  const [repos, setRepos] = useState<RepoOption[]>([]);
  const [selected, setSelected] = useState<string>(currentRepoId(node) || "");
  const stored: string[] = node?.repo_paths || [];
  const [paths, setPaths] = useState<string>(stored.join("\n"));
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  useEffect(() => setSelected(currentRepoId(node) || ""), [node]);
  // The page that drew this part may not have loaded its repository link,
  // and an absent link reads as "inherit the parent's" (reviews 2026-11-05):
  // the part is read once more WITH its link (`rflp-software-part-by-id`
  // and its optional `realized_by_repo` include, into the graph store), then shown.
  useEffect(() => {
    const id = node?.id;
    if (!id) return;
    let live = true;
    void SoftwarePart.setQuery(QueryNames.rflpSoftwarePartById)
      .get(id)
      .load({ realized_by_repo: {} })
      .first()
      .then(() => {
        if (live) setSelected(currentRepoId(node) || "");
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [node?.id]);
  useEffect(() => setPaths((node?.repo_paths || []).join("\n")), [node?.repo_paths]);

  useEffect(() => {
    let live = true;
    void (async () => {
      const orgId = await readActiveOrganisationId().catch(() => null);
      if (!orgId) return;
      const rows = await GitHubRepo.setQuery(QueryNames.rflpOrganisationGithubRepos)
        .filter({ org_id: orgId })
        .all()
        .catch(() => []);
      if (live) setRepos(rows.map((r: any) => ({ id: r.id, name: r.name, private: r.private, connected: r.connected })));
    })();
    return () => {
      live = false;
    };
  }, []);

  // A refused save shows what is SAVED, never the attempt (reviews
  // 2026-11-05): `restore` puts the saved repository and paths back, on
  // screen and in the editable, so a later save never re-sends the refused
  // change.
  const save = async (apply: () => void, restore: () => void) => {
    if (!editable) return;
    setSaving(true);
    try {
      apply();
      await editable.save();
    } catch (e) {
      restore();
      setRefusal(toRefusal(e, "The repository could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  const onSelectRepo = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const repoId = e.target.value;
    const saved = currentRepoId(node);
    setSelected(repoId);
    void save(
      () => editable.realized_by_repo.set(repoId ? { id: repoId } : null),
      () => {
        editable.realized_by_repo.set(saved ? { id: saved } : null);
        setSelected(saved || "");
      },
    );
  };

  const commitPaths = () => {
    const next = parsePaths(paths);
    if (next.join("\n") === stored.join("\n")) return;
    const saved = [...stored];
    void save(
      () => {
        editable.repo_paths = next;
      },
      () => {
        editable.repo_paths = saved;
        setPaths(saved.join("\n"));
      },
    );
  };

  const choices = repos.filter((r) => r.connected || r.id === selected);

  return (
    <div className="space-y-2" data-testid="softwarepart-repo-editor">
      {refusal && <RefusalNotice refusal={refusal} onDismiss={() => setRefusal(null)} />}
      <div className="flex items-center gap-2">
        <select
          data-testid="repo-select"
          value={selected}
          onChange={onSelectRepo}
          disabled={!editable || saving}
          className="rounded border border-gray-300 px-2 py-1 text-sm"
        >
          <option value="">{choices.length ? "— inherit the parent's repository —" : "No connected repositories"}</option>
          {choices.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
              {r.private ? " (private)" : ""}
              {r.connected ? "" : " (disconnected)"}
            </option>
          ))}
        </select>
        {saving && <span className="text-xs text-gray-400">saving…</span>}
        <Link href="/connect-repo" className="text-xs text-gray-500 hover:underline">
          Connect repositories
        </Link>
      </div>
      <div>
        <span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-gray-400">
          Paths in repository (one per line; empty = the whole repository)
        </span>
        <textarea
          data-testid="repo-paths-input"
          value={paths}
          rows={Math.max(2, parsePaths(paths).length + 1)}
          onChange={(e) => setPaths(e.target.value)}
          onBlur={commitPaths}
          disabled={!editable || saving}
          placeholder={"e.g. src/motor/\nproto/motor.proto"}
          className="w-full rounded border border-gray-300 px-2 py-1 font-mono text-sm"
        />
      </div>
    </div>
  );
}
